import { memo, useCallback, useMemo, useState } from 'react';

import { EllipsisIcon } from '@air/design-icons';
import { ActionSheet, renderActionSheetItems } from '@air/primitive-action-sheet';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';
import { useShortIdContext } from '@air/provider-short-id';

import { MobileAssetModalNavigationItem } from '~/_components/MobileAssetModalNavigationItem';
import { SharePublicClipModal } from '~/components/Modals/SharePublicClipModal';
import { usePublicDownloadOptions } from '~/components/PublicAssetModal/shared/hooks/usePublicDownloadOptions';
import { centralizedClipIdSelector } from '~/store/centralizedClip/selectors';
import { getPublicClipShareUrl } from '~/swr-hooks/shortUrl/utils';
import { useAirStore } from '~/utils/ReduxUtils';

export const PublicAssetMobileNavigationEllipsisMenu = memo(() => {
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);
  const downloadOptions = usePublicDownloadOptions();
  const [sharePublicClip] = useAirModal(SharePublicClipModal);
  const store = useAirStore();
  const { shortId } = useShortIdContext();

  const handleShareClick = useCallback(() => {
    const clipId = centralizedClipIdSelector(store.getState());

    if (!clipId) {
      return;
    }

    sharePublicClip({
      id: clipId,
      url: getPublicClipShareUrl(shortId),
      trackLocation: 'public-asset-page',
    });
  }, [sharePublicClip, shortId, store]);

  const handleOpenActionSheet = useCallback(() => setIsActionSheetOpen(true), []);
  const handleCloseActionSheet = useCallback(() => setIsActionSheetOpen(false), []);

  const options = useMemo<DropdownMenuOption[]>(() => {
    const dismissThenSelect = (onSelect: (() => void) | undefined) => () => {
      // The sheet must be dismissed before the action runs: options open modals built
      // on the legacy @reach/dialog Modal, and a mounted Radix ActionSheet keeps
      // `pointer-events: none` on <body>, which makes those modals tap-through on mobile.
      setIsActionSheetOpen(false);
      onSelect?.();
    };

    return [
      {
        id: 'share',
        label: 'Share',
        onSelect: dismissThenSelect(handleShareClick),
        type: 'item',
      },
      ...downloadOptions.map((option) =>
        option.type === 'item' ? { ...option, onSelect: dismissThenSelect(option.onSelect) } : option,
      ),
    ];
  }, [downloadOptions, handleShareClick]);

  if (!options.length) {
    return null;
  }

  return (
    <>
      <MobileAssetModalNavigationItem
        adsIcon
        id="more-options"
        icon={EllipsisIcon}
        label="More options"
        onClick={handleOpenActionSheet}
      />

      {isActionSheetOpen && (
        <ActionSheet onClose={handleCloseActionSheet}>{renderActionSheetItems({ options })}</ActionSheet>
      )}
    </>
  );
});

PublicAssetMobileNavigationEllipsisMenu.displayName = 'PublicAssetMobileNavigationEllipsisMenu';
